/**
 * storage.js - Gestion du stockage local des données
 *
 * VERSION REFACTORISÉE - 100% basée sur data[]
 * ================================================
 *
 * Suppression complète de fiberData au profit d'une structure unifiée data[]
 * Format unique pour import/export avec cohérence garantie
 *
 * Structure data[] :
 * [
 *   { tiroir: "T1", module: "A", port: 1, statut: "LIBRE" },
 *   { tiroir: "T1", module: "A", port: 2, statut: "OCCUPÉE" },
 *   ...
 * ]
 *
 * @author ITE Technologies
 * @version 2.0.0
 * @since 2025-01-17
 */

import { STORAGE_KEYS, generateExportFilename } from "./constants.js";

// ===== GESTION DES PROJETS =====

/**
 * Sauvegarder un projet complet dans localStorage
 *
 * @param {Object} project - Projet à sauvegarder
 * @param {string} project.name - Nom du projet
 * @param {number} project.fiberType - Type de fibre (12 ou 24)
 * @param {Array<number>} project.activeTiroirs - Tiroirs actifs [1,2,3,4]
 * @param {Object} project.activeModules - Modules actifs par tiroir
 * @param {Array<Object>} project.data - Données des fibres
 * @returns {boolean} Succès de la sauvegarde
 */
export function saveProject(project) {
  try {
    // Enrichir avec métadonnées
    const projectToSave = {
      ...project,
      lastModified: new Date().toISOString(),
    };

    // Valider les données avant sauvegarde
    const validation = validateProjectData(projectToSave);
    if (!validation.isValid) {
      console.warn("⚠️ Données incohérentes détectées:", validation.errors);
      // Auto-correction si possible
      projectToSave.data = ensureCompleteData(projectToSave);
    }

    // Sauvegarder dans localStorage
    localStorage.setItem(STORAGE_KEYS.currentProject, JSON.stringify(projectToSave));

    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la sauvegarde du projet:", error);
    return false;
  }
}

/**
 * Charger le projet actuel depuis localStorage
 *
 * @returns {Object|null} Projet chargé ou null si inexistant
 */
export function loadCurrentProject() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.currentProject);
    if (!data) return null;

    const project = JSON.parse(data);

    // Migration automatique des anciens formats
    const migratedProject = migrateLegacyProject(project);

    // Si migration effectuée, sauvegarder le nouveau format
    if (migratedProject !== project) {
      saveProject(migratedProject);
    }

    return migratedProject;
  } catch (error) {
    console.error("❌ Erreur lors du chargement du projet:", error);
    return null;
  }
}

// ===== UTILITAIRES DATA[] =====

/**
 * Obtenir tous les statuts d'un module sous forme de tableau
 *
 * @param {Array<Object>} data - Données du projet
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @param {number} fiberType - Nombre de fibres par module (12 ou 24)
 * @returns {Array<string>} Tableau des statuts [statut1, statut2, ...]
 */
export function getModuleStatuses(data, tiroir, module, fiberType = 24) {
  // Récupérer les données du module triées par port
  const moduleData = data
    .filter((row) => row.tiroir === tiroir && row.module === module)
    .sort((a, b) => a.port - b.port);

  // Créer un tableau complet avec N/A par défaut
  const statuses = new Array(fiberType).fill("N/A");

  // Remplir avec les données existantes
  moduleData.forEach((row) => {
    if (row.port >= 1 && row.port <= fiberType) {
      statuses[row.port - 1] = row.statut;
    }
  });

  return statuses;
}

/**
 * Obtenir le statut d'une fibre spécifique
 *
 * @param {Array<Object>} data - Données du projet
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @param {number} port - Numéro du port (1-24)
 * @returns {string} Statut de la fibre ("N/A", "LIBRE", "OCCUPÉE")
 */
export function getFiberStatus(data, tiroir, module, port) {
  const fiber = data.find((row) => row.tiroir === tiroir && row.module === module && row.port === port);
  return fiber?.statut || "N/A";
}

/**
 * Modifier le statut d'une fibre spécifique
 *
 * @param {Array<Object>} data - Données du projet (modifié par référence)
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @param {number} port - Numéro du port (1-24)
 * @param {string} newStatus - Nouveau statut ("N/A", "LIBRE", "OCCUPÉE")
 * @returns {boolean} Succès de la modification
 */
export function setFiberStatus(data, tiroir, module, port, newStatus) {
  try {
    // Chercher l'entrée existante
    const index = data.findIndex((row) => row.tiroir === tiroir && row.module === module && row.port === port);

    if (index >= 0) {
      // Modifier l'entrée existante
      data[index].statut = newStatus.toUpperCase();
    } else {
      // Créer nouvelle entrée
      data.push({
        tiroir,
        module,
        port,
        statut: newStatus.toUpperCase(),
      });
    }

    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la modification de fibre:", error);
    return false;
  }
}

/**
 * Modifier tous les statuts d'un module
 *
 * @param {Array<Object>} data - Données du projet (modifié par référence)
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @param {Array<string>} newStatuses - Nouveaux statuts pour toutes les fibres
 * @returns {boolean} Succès de la modification
 */
export function setModuleStatuses(data, tiroir, module, newStatuses) {
  try {
    // Supprimer toutes les entrées existantes du module
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i].tiroir === tiroir && data[i].module === module) {
        data.splice(i, 1);
      }
    }

    // Ajouter les nouveaux statuts
    newStatuses.forEach((statut, index) => {
      if (statut !== "N/A") {
        // Optimisation: ne stocker que les fibres configurées
        data.push({
          tiroir,
          module,
          port: index + 1,
          statut: statut.toUpperCase(),
        });
      }
    });

    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la modification du module:", error);
    return false;
  }
}

/**
 * Calculer les statistiques d'un module
 *
 * @param {Array<Object>} data - Données du projet
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {string} module - Lettre du module (ex: "A")
 * @param {number} fiberType - Nombre de fibres par module (12 ou 24)
 * @returns {Object} Statistiques du module
 */
export function getModuleStats(data, tiroir, module, fiberType = 24) {
  const moduleData = data.filter((row) => row.tiroir === tiroir && row.module === module);
  const libres = moduleData.filter((row) => row.statut === "LIBRE").length;
  const occupees = moduleData.filter((row) => row.statut === "OCCUPÉE").length;
  const configured = libres + occupees;

  return {
    total: fiberType,
    configured,
    libres,
    occupees,
    percentage: Math.round((configured / fiberType) * 100),
  };
}

/**
 * Calculer les statistiques d'un tiroir
 *
 * @param {Array<Object>} data - Données du projet
 * @param {string} tiroir - Identifiant tiroir (ex: "T1")
 * @param {Array<string>} activeModules - Modules actifs ["A", "B", "C"]
 * @param {number} fiberType - Nombre de fibres par module (12 ou 24)
 * @returns {Object} Statistiques du tiroir
 */
export function getTiroirStats(data, tiroir, activeModules, fiberType = 24) {
  const tiroirData = data.filter((row) => row.tiroir === tiroir && activeModules.includes(row.module));

  const libres = tiroirData.filter((row) => row.statut === "LIBRE").length;
  const occupees = tiroirData.filter((row) => row.statut === "OCCUPÉE").length;
  const configured = libres + occupees;
  const total = activeModules.length * fiberType;

  return {
    total,
    configured,
    libres,
    occupees,
    percentage: total > 0 ? Math.round((configured / total) * 100) : 0,
  };
}

// ===== GESTION DES TIROIRS ET MODULES =====

/**
 * Activer/désactiver un tiroir
 *
 * @param {number} tiroirNum - Numéro du tiroir (1-4)
 * @param {boolean} isActive - État souhaité
 * @returns {boolean} Succès de l'opération
 */
export function toggleTiroir(tiroirNum, isActive) {
  try {
    const project = loadCurrentProject();
    if (!project) return false;

    if (isActive) {
      // Activer le tiroir
      if (!project.activeTiroirs.includes(tiroirNum)) {
        project.activeTiroirs.push(tiroirNum);
        project.activeTiroirs.sort();

        // Initialiser les modules actifs pour ce tiroir
        const tiroirKey = `T${tiroirNum}`;
        if (!project.activeModules) {
          project.activeModules = {};
        }
        if (!project.activeModules[tiroirKey]) {
          // Tous les modules actifs par défaut selon le type de fibre
          const moduleLetters =
            project.fiberType === 24
              ? ["A", "B", "C", "D", "E", "F"]
              : ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
          project.activeModules[tiroirKey] = [...moduleLetters];
        }
      }
    } else {
      // Désactiver le tiroir
      project.activeTiroirs = project.activeTiroirs.filter((t) => t !== tiroirNum);

      // Optionnel: nettoyer les données du tiroir désactivé
      // project.data = project.data.filter(row => row.tiroir !== `T${tiroirNum}`);
    }

    return saveProject(project);
  } catch (error) {
    console.error("❌ Erreur lors du toggle tiroir:", error);
    return false;
  }
}

/**
 * Activer/désactiver un module avec reset des fibres
 *
 * @param {number} tiroirNum - Numéro du tiroir (1-4)
 * @param {string} moduleLetter - Lettre du module (A-L)
 * @param {boolean} isActive - État souhaité
 * @returns {boolean} Succès de l'opération
 */
export function toggleModule(tiroirNum, moduleLetter, isActive) {
  try {
    const project = loadCurrentProject();
    if (!project) return false;

    const tiroirKey = `T${tiroirNum}`;

    // Initialiser les structures si nécessaire
    if (!project.activeModules) project.activeModules = {};
    if (!project.activeModules[tiroirKey]) project.activeModules[tiroirKey] = [];
    if (!project.data) project.data = [];

    if (isActive) {
      // Activer le module
      if (!project.activeModules[tiroirKey].includes(moduleLetter)) {
        project.activeModules[tiroirKey].push(moduleLetter);
        project.activeModules[tiroirKey].sort();
      }
    } else {
      // Désactiver le module
      project.activeModules[tiroirKey] = project.activeModules[tiroirKey].filter((m) => m !== moduleLetter);

      // Reset toutes les fibres du module à N/A (suppression des entrées)
      project.data = project.data.filter((row) => !(row.tiroir === tiroirKey && row.module === moduleLetter));
    }

    return saveProject(project);
  } catch (error) {
    console.error("❌ Erreur lors du toggle module:", error);
    return false;
  }
}

// ===== MIGRATION ET VALIDATION =====

/**
 * Migrer un projet de l'ancien format vers le nouveau
 *
 * @param {Object} project - Projet à migrer
 * @returns {Object} Projet migré
 */
function migrateLegacyProject(project) {
  // Si le projet a déjà le nouveau format, rien à faire
  if (!project.fiberData || project.data) {
    return project;
  }

  // Créer le tableau data[] à partir de fiberData
  project.data = [];

  Object.entries(project.fiberData).forEach(([moduleKey, moduleData]) => {
    const [tiroir, module] = moduleKey.split("-");

    if (moduleData.statuses) {
      moduleData.statuses.forEach((statut, index) => {
        // Ne stocker que les fibres configurées (optimisation)
        if (statut !== "N/A") {
          project.data.push({
            tiroir,
            module,
            port: index + 1,
            statut: statut.toUpperCase(),
          });
        }
      });
    }
  });

  // Supprimer l'ancien format
  delete project.fiberData;

  return project;
}

/**
 * Valider la cohérence des données d'un projet
 *
 * @param {Object} project - Projet à valider
 * @returns {Object} Résultat de la validation
 */
function validateProjectData(project) {
  const errors = [];

  if (!project.data) {
    errors.push("Données manquantes (data[])");
    return { isValid: false, errors };
  }

  // Vérifier que tous les tiroirs actifs ont des modules
  project.activeTiroirs?.forEach((tiroirNum) => {
    const tiroir = `T${tiroirNum}`;
    const activeModules = project.activeModules?.[tiroir] || [];

    if (activeModules.length === 0) {
      errors.push(`Tiroir ${tiroir}: aucun module actif`);
    }

    // Vérifier l'intégrité des données pour chaque module actif
    activeModules.forEach((module) => {
      const moduleData = project.data.filter((row) => row.tiroir === tiroir && row.module === module);

      // Vérifier les numéros de ports
      const invalidPorts = moduleData.filter((row) => row.port < 1 || row.port > project.fiberType);

      if (invalidPorts.length > 0) {
        errors.push(`Module ${tiroir}-${module}: ports invalides détectés`);
      }

      // Vérifier les doublons
      const ports = moduleData.map((row) => row.port);
      const duplicates = ports.filter((port, index) => ports.indexOf(port) !== index);
      if (duplicates.length > 0) {
        errors.push(`Module ${tiroir}-${module}: ports dupliqués ${duplicates.join(", ")}`);
      }
    });
  });

  return { isValid: errors.length === 0, errors };
}

/**
 * S'assurer que toutes les données nécessaires existent
 *
 * @param {Object} project - Projet à compléter
 * @returns {Array<Object>} Données complètes
 */
function ensureCompleteData(project) {
  const data = [...(project.data || [])];

  // Pour chaque tiroir actif et module actif, s'assurer qu'il y a au moins les entrées N/A
  project.activeTiroirs?.forEach((tiroirNum) => {
    const tiroir = `T${tiroirNum}`;
    const activeModules = project.activeModules?.[tiroir] || [];

    activeModules.forEach((module) => {
      for (let port = 1; port <= project.fiberType; port++) {
        const exists = data.find((row) => row.tiroir === tiroir && row.module === module && row.port === port);

        if (!exists) {
          // Ne pas ajouter les N/A pour optimiser la taille
          // data.push({ tiroir, module, port, statut: "N/A" });
        }
      }
    });
  });

  return data;
}

// ===== EXPORT/IMPORT =====

/**
 * Exporter un projet vers un fichier JSON
 *
 * @param {Object|null} project - Projet à exporter (ou null pour le projet actuel)
 * @returns {Object} Résultat de l'export
 */
export function exportProjectToJSON(project = null) {
  try {
    // Utiliser le projet actuel si non spécifié
    if (!project) {
      project = loadCurrentProject();
      if (!project) {
        throw new Error("Aucun projet à exporter");
      }
    }

    // S'assurer que les données sont triées
    const sortedData = [...(project.data || [])]
      .filter((entry) => entry.statut === "OCCUPÉE" || entry.statut === "LIBRE")
      .sort((a, b) => {
        if (a.tiroir !== b.tiroir) return a.tiroir.localeCompare(b.tiroir);
        if (a.module !== b.module) return a.module.localeCompare(b.module);
        return a.port - b.port;
      });

      const libre = sortedData.filter((row) => row.statut === "LIBRE").length;
      const occupee = sortedData.filter((row) => row.statut === "OCCUPÉE").length;
      const configured = sortedData.length;

      const summary = {
        configured: configured,
        libre: libre,
        occupee: occupee,
        na: Math.max(configured - libre - occupee, 0),
      };

    // Créer la structure d'export unifiée
    const exportData = {
      metadata: {
        // type 12 or 24 configuration
        configurationType: project.fiberType,
        projetName: project.name,
        activeTiroirs: project.activeTiroirs || [],
        activeModules: project.activeModules || {},
        exportDate: new Date().toISOString(),
        version: "2.0.0",
        application: "Configuration des Tiroirs Fibres Optiques",
        format: "unified-data-array",
        totalFibers: sortedData.length,
      },
      summary: summary,
      project: {
        // ... reste inchangé
      },
      data: sortedData,
    };

    // Générer le nom de fichier
    const filename = generateExportFilename(project.name);

    // Créer et télécharger le fichier
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { success: true, filename, totalFibers: sortedData.length };
  } catch (error) {
    console.error("❌ Erreur lors de l'export:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Importer un projet depuis un fichier JSON
 *
 * @param {string} jsonString - Contenu JSON du fichier
 * @returns {Object} Résultat de l'import
 */
export function importProjectFromJSON(jsonString) {
  try {
    const data = JSON.parse(jsonString);

    // Valider structure minimale
    if (!data.metadata || !data.data) {
      throw new Error("Format invalide - metadata ou data manquants");
    }

    // Récupérer activeModules depuis metadata
    let activeModules = data.metadata.activeModules || {};

    // Si vide, reconstruire avec TOUS les modules par défaut
    if (Object.keys(activeModules).length === 0) {
      const fiberType = data.metadata.configurationType || 24;
      const allModules =
        fiberType === 24
          ? ["A", "B", "C", "D", "E", "F"]
          : ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

      // Initialiser avec TOUS les modules actifs pour chaque tiroir
      (data.metadata.activeTiroirs || []).forEach((tiroirNum) => {
        activeModules[`T${tiroirNum}`] = [...allModules];
      });
    }

    // Créer le projet avec les données correctes
    const project = {
      name: data.metadata.projetName || "Projet Importé",
      fiberType: data.metadata.configurationType || 24,
      activeTiroirs: [...(data.metadata.activeTiroirs || [1, 2, 3, 4])],
      activeModules: activeModules,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      data: [...(data.data || [])],
    };

    // Sauvegarder le projet importé
    const success = saveProject(project);

    if (success) {
      return { success: true, project };
    } else {
      throw new Error("Échec sauvegarde");
    }
  } catch (error) {
    console.error("❌ Erreur import:", error);
    return { success: false, error: error.message };
  }
}

// ===== UTILITAIRES GÉNÉRAUX =====

/**
 * Vider toutes les données stockées
 *
 * @returns {boolean} Succès de l'opération
 */
export function clearAllData() {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    return true;
  } catch (error) {
    console.error("❌ Erreur lors de la suppression:", error);
    return false;
  }
}

/**
 * Obtenir la taille des données stockées
 *
 * @returns {string} Taille en KB
 */
export function getStorageSize() {
  try {
    let total = 0;
    Object.values(STORAGE_KEYS).forEach((key) => {
      const data = localStorage.getItem(key);
      if (data) {
        total += new Blob([data]).size;
      }
    });
    return (total / 1024).toFixed(2);
  } catch (error) {
    console.error("❌ Erreur lors du calcul de taille:", error);
    return "0";
  }
}
